// components/referral/ReferralDashboard.jsx
import React, { useState, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import ReferralStatus from './ReferralStatus.jsx';
import ReferralManagement from './ReferralManagement.jsx';

const ReferralDashboard = ({ user }) => {
  const [referralData, setReferralData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creatingCode, setCreatingCode] = useState(false);
  const [customCode, setCustomCode] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);

  const functions = getFunctions();

  useEffect(() => {
    if (user) {
      loadReferralData();
    }
  }, [user]);

  const loadReferralData = async () => {
    try {
      setLoading(true);
      const getReferralAnalytics = httpsCallable(functions, 'getReferralAnalytics');
      const result = await getReferralAnalytics();

      if (result.data.success) {
        setReferralData(result.data.analytics);
      } else {
        setError('Failed to load referral data');
      }
    } catch (error) {
      console.error('Error loading referral data:', error);
      setError('Failed to load referral data');
    } finally {
      setLoading(false);
    }
  };

  const createReferralCode = async () => {
    try {
      setCreatingCode(true);
      setError(null);

      const createOrUpdateReferralCode = httpsCallable(functions, 'createOrUpdateReferralCode');
      const result = await createOrUpdateReferralCode({ 
        customCode: customCode.trim() || null 
      });

      if (result.data.success) {
        setCustomCode('');
        setShowCreateForm(false);
        await loadReferralData(); // Refresh data
      } else {
        setError(result.data.error || 'Failed to create referral code');
      }
    } catch (error) {
      console.error('Error creating referral code:', error);
      setError('Failed to create referral code');
    } finally {
      setCreatingCode(false);
    }
  };

  const regenerateCode = async () => {
    if (!window.confirm('Are you sure you want to regenerate your referral code? Your old code will stop working.')) {
      return;
    }

    try {
      setCreatingCode(true);
      setError(null);

      const regenerateReferralCode = httpsCallable(functions, 'regenerateReferralCode');
      const result = await regenerateReferralCode({ 
        newCustomCode: customCode.trim() || null 
      });

      if (result.data.success) {
        setCustomCode('');
        setShowCreateForm(false);
        await loadReferralData(); // Refresh data
      } else {
        setError(result.data.error || 'Failed to regenerate referral code');
      }
    } catch (error) {
      console.error('Error regenerating referral code:', error);
      setError('Failed to regenerate referral code');
    } finally {
      setCreatingCode(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      // Show temporary success message
      const originalText = document.getElementById('copy-button').textContent;
      document.getElementById('copy-button').textContent = 'Copied!';
      setTimeout(() => {
        document.getElementById('copy-button').textContent = originalText;
      }, 2000);
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount || 0);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    // Handle different timestamp formats
    if (timestamp.seconds) {
      // Firestore timestamp format
      return new Date(timestamp.seconds * 1000).toLocaleDateString();
    } else if (timestamp._seconds) {
      // Alternative Firestore timestamp format
      return new Date(timestamp._seconds * 1000).toLocaleDateString();
    } else if (typeof timestamp === 'string' || typeof timestamp === 'number') {
      // ISO string or number timestamp
      return new Date(timestamp).toLocaleDateString();
    } else if (timestamp instanceof Date) {
      // Already a Date object
      return timestamp.toLocaleDateString();
    }
    
    return 'Invalid Date';
  };

  if (loading) {
    return (
      <div style={{
        padding: '40px',
        textAlign: 'center',
        backgroundColor: '#1F2937',
        borderRadius: '20px',
        border: '4px solid #374151'
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          border: '4px solid #374151',
          borderTop: '4px solid #10B981',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 20px'
        }}></div>
        <p style={{ color: '#9CA3AF', margin: 0 }}>Loading referral data...</p>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  return (
    <div style={{ padding: '0' }}>
      {/* Show ReferralStatus at top if no referral code exists */}
      {!referralData?.hasReferralCode && (
        <>
          <ReferralStatus user={user} />
          <div style={{ height: '24px' }}></div>
        </>
      )}
      
      <div style={{
        backgroundColor: '#1F2937',
        borderRadius: '20px',
        padding: '32px',
        border: '4px solid #374151',
        marginBottom: '24px'
      }}>
        <h2 style={{
          fontSize: '28px',
          fontWeight: 'bold',
          margin: '0 0 8px 0',
          color: '#10B981',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <span>💎</span>
          Referral Program
        </h2>
        <p style={{
          color: '#9CA3AF',
          fontSize: '16px',
          margin: '0 0 24px 0',
          lineHeight: '1.5'
        }}>
          Earn 2% commission from musicians you refer for 12 months after their first paid performance!
        </p>

        {error && (
          <div style={{
            backgroundColor: '#7F1D1D',
            border: '2px solid #DC2626',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '24px',
            color: '#FCA5A5',
            fontSize: '14px'
          }}>
            ❌ {error}
          </div>
        )}

        {/* Referral Code Section */}
        {referralData?.hasReferralCode ? (
          <div style={{
            backgroundColor: '#065F46',
            borderRadius: '16px',
            padding: '24px',
            border: '3px solid #10B981',
            marginBottom: '24px'
          }}>
            <h3 style={{
              color: '#6EE7B7',
              fontSize: '20px',
              fontWeight: 'bold',
              margin: '0 0 16px 0',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span>🎯</span>
              Your Referral Code
            </h3>
            
            <div style={{
              backgroundColor: '#064E3B',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '16px',
              border: '2px solid #059669'
            }}>
              <div style={{
                fontSize: '32px',
                fontWeight: 'bold',
                color: '#6EE7B7',
                textAlign: 'center',
                fontFamily: 'monospace',
                letterSpacing: '4px',
                marginBottom: '12px'
              }}>
                {referralData.referralCode}
              </div>
              
              <div style={{
                display: 'flex',
                gap: '12px',
                flexWrap: 'wrap'
              }}>
                <button
                  id="copy-button"
                  onClick={() => copyToClipboard(referralData.referralCode)}
                  style={{
                    flex: 1,
                    minWidth: '120px',
                    padding: '12px 20px',
                    backgroundColor: '#10B981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    transition: 'background-color 0.3s ease'
                  }}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#059669'}
                  onMouseOut={(e) => e.target.style.backgroundColor = '#10B981'}
                >
                  📋 Copy Code
                </button>
                
                <button
                  onClick={() => setShowCreateForm(!showCreateForm)}
                  style={{
                    flex: 1,
                    minWidth: '120px',
                    padding: '12px 20px',
                    backgroundColor: '#374151',
                    color: '#9CA3AF',
                    border: '2px solid #4B5563',
                    borderRadius: '8px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = '#4B5563';
                    e.target.style.color = 'white';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = '#374151';
                    e.target.style.color = '#9CA3AF';
                  }}
                >
                  🔄 Change Code
                </button>
              </div>
            </div>

            <div style={{
              color: '#A7F3D0',
              fontSize: '14px',
              lineHeight: '1.4'
            }}>
              💡 <strong>Share this code</strong> with other musicians so they can enter it during onboarding.
            </div>
          </div>
        ) : (
          <div style={{
            backgroundColor: '#374151',
            borderRadius: '16px',
            padding: '24px',
            border: '3px solid #4B5563',
            marginBottom: '24px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎯</div>
            <h3 style={{
              color: '#C4B5FD',
              fontSize: '20px',
              fontWeight: 'bold',
              margin: '0 0 12px 0'
            }}>
              Create Your Referral Code
            </h3>
            <p style={{
              color: '#9CA3AF',
              fontSize: '16px',
              margin: '0 0 20px 0',
              lineHeight: '1.5'
            }}>
              Get a personalized code to share with other musicians and start earning commissions!
            </p>
            
            <button
              onClick={() => setShowCreateForm(true)}
              style={{
                padding: '16px 32px',
                backgroundColor: '#10B981',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '18px',
                fontWeight: 'bold',
                cursor: 'pointer',
                transition: 'background-color 0.3s ease'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#059669'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#10B981'}
            >
              🚀 Create Referral Code
            </button>
          </div>
        )}

        {/* Create/Regenerate Form */}
        {showCreateForm && (
          <div style={{
            backgroundColor: '#374151',
            borderRadius: '16px',
            padding: '24px',
            border: '3px solid #4B5563',
            marginBottom: '24px'
          }}>
            <h3 style={{
              color: '#C4B5FD',
              fontSize: '18px',
              fontWeight: 'bold',
              margin: '0 0 16px 0'
            }}>
              {referralData?.hasReferralCode ? 'Change Referral Code' : 'Create Referral Code'}
            </h3>
            
            <div style={{ marginBottom: '16px' }}>
              <input
                type="text"
                placeholder="Enter custom code (optional, e.g. MUSICLOVER)"
                value={customCode}
                onChange={(e) => setCustomCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').substring(0, 12))}
                style={{
                  width: '100%',
                  padding: '16px',
                  fontSize: '16px',
                  border: '2px solid #4B5563',
                  borderRadius: '8px',
                  backgroundColor: '#1F2937',
                  color: 'white',
                  outline: 'none'
                }}
              />
              <p style={{
                color: '#9CA3AF',
                fontSize: '12px',
                margin: '8px 0 0 0'
              }}>
                Leave empty to auto-generate based on your name. Max 12 characters, letters and numbers only.
              </p>
            </div>

            <div style={{
              display: 'flex',
              gap: '12px'
            }}>
              <button
                onClick={referralData?.hasReferralCode ? regenerateCode : createReferralCode}
                disabled={creatingCode}
                style={{
                  flex: 1,
                  padding: '12px 20px',
                  backgroundColor: creatingCode ? '#4B5563' : '#10B981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 'bold',
                  cursor: creatingCode ? 'not-allowed' : 'pointer',
                  transition: 'background-color 0.3s ease'
                }}
              >
                {creatingCode ? '⏳ Processing...' : 
                 referralData?.hasReferralCode ? '🔄 Update Code' : '✨ Create Code'}
              </button>
              
              <button
                onClick={() => {
                  setShowCreateForm(false);
                  setCustomCode('');
                  setError(null);
                }}
                style={{
                  padding: '12px 20px',
                  backgroundColor: '#6B7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'background-color 0.3s ease'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#4B5563'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#6B7280'}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Stats Overview */}
        {referralData?.hasReferralCode && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            marginBottom: '24px'
          }}>
            <div style={{
              backgroundColor: '#1E40AF',
              borderRadius: '12px',
              padding: '20px',
              border: '3px solid #3B82F6',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '24px', marginBottom: '8px' }}>👥</div>
              <div style={{
                fontSize: '32px',
                fontWeight: 'bold',
                color: '#93C5FD',
                marginBottom: '4px'
              }}>
                {referralData.totalReferrals}
              </div>
              <div style={{
                color: '#BFDBFE',
                fontSize: '14px',
                fontWeight: 'bold'
              }}>
                Total Referrals
              </div>
            </div>

            <div style={{
              backgroundColor: '#065F46',
              borderRadius: '12px',
              padding: '20px',
              border: '3px solid #10B981',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '24px', marginBottom: '8px' }}>⚡</div>
              <div style={{
                fontSize: '32px',
                fontWeight: 'bold',
                color: '#6EE7B7',
                marginBottom: '4px'
              }}>
                {referralData.activeReferrals}
              </div>
              <div style={{
                color: '#A7F3D0',
                fontSize: '14px',
                fontWeight: 'bold'
              }}>
                Active Referrals
              </div>
            </div>

            <div style={{
              backgroundColor: '#7C2D12',
              borderRadius: '12px',
              padding: '20px',
              border: '3px solid #EA580C',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '24px', marginBottom: '8px' }}>💰</div>
              <div style={{
                fontSize: '32px',
                fontWeight: 'bold',
                color: '#FDBA74',
                marginBottom: '4px'
              }}>
                {formatCurrency(referralData.totalCommissions)}
              </div>
              <div style={{
                color: '#FED7AA',
                fontSize: '14px',
                fontWeight: 'bold'
              }}>
                Total Earned
              </div>
            </div>
          </div>
        )}

        {/* Referred Musicians List */}
        {referralData?.hasReferralCode && referralData?.referredMusicians?.length > 0 && (
          <div style={{
            backgroundColor: '#374151',
            borderRadius: '16px',
            padding: '24px',
            border: '3px solid #4B5563',
            marginBottom: '24px'
          }}>
            <h3 style={{
              color: '#C4B5FD',
              fontSize: '20px',
              fontWeight: 'bold',
              margin: '0 0 16px 0',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span>👥</span>
              Musicians In Your Referral Network ({referralData.referredMusicians.length})
            </h3>

            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {referralData.referredMusicians.map((musician, index) => (
                <div
                  key={index}
                  style={{
                    backgroundColor: '#1F2937',
                    borderRadius: '12px',
                    padding: '20px',
                    marginBottom: '16px',
                    border: '2px solid #374151',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '16px'
                  }}
                >
                  <div style={{ flex: 1, minWidth: '200px' }}>
                    <div style={{
                      color: '#10B981',
                      fontWeight: 'bold',
                      fontSize: '18px',
                      marginBottom: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span>🎤</span>
                      {musician.displayName || 'New Musician'}
                    </div>
                    <div style={{
                      color: '#9CA3AF',
                      fontSize: '14px',
                      marginBottom: '8px'
                    }}>
                      Status: <span style={{
                        color: musician.status === 'active' ? '#10B981' : '#F59E0B',
                        fontWeight: 'bold',
                        textTransform: 'capitalize'
                      }}>
                        {musician.status}
                      </span>
                    </div>
                    <div style={{
                      color: '#6B7280',
                      fontSize: '12px'
                    }}>
                      Joined: {formatDate(musician.createdAt)}
                    </div>
                  </div>
                  
                  <div style={{ 
                    textAlign: 'right',
                    minWidth: '150px'
                  }}>
                    <div style={{
                      color: '#10B981',
                      fontWeight: 'bold',
                      fontSize: '20px',
                      marginBottom: '4px'
                    }}>
                      {formatCurrency(musician.totalEarnings || 0)}
                    </div>
                    <div style={{
                      color: '#9CA3AF',
                      fontSize: '14px',
                      marginBottom: '4px'
                    }}>
                      Their Total Earnings
                    </div>
                    <div style={{
                      color: '#F59E0B',
                      fontSize: '16px',
                      fontWeight: 'bold'
                    }}>
                      Your Commission: {formatCurrency(musician.yourCommission || 0)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Activity */}
        {referralData?.recentCommissions?.length > 0 && (
          <div style={{
            backgroundColor: '#374151',
            borderRadius: '16px',
            padding: '24px',
            border: '3px solid #4B5563'
          }}>
            <h3 style={{
              color: '#C4B5FD',
              fontSize: '20px',
              fontWeight: 'bold',
              margin: '0 0 16px 0',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span>📈</span>
              Recent Commissions
            </h3>

            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {referralData.recentCommissions.map((commission, index) => (
                <div
                  key={index}
                  style={{
                    backgroundColor: '#1F2937',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '12px',
                    border: '2px solid #374151',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <div>
                    <div style={{
                      color: '#10B981',
                      fontWeight: 'bold',
                      fontSize: '18px',
                      marginBottom: '4px'
                    }}>
                      +{formatCurrency(commission.amount)}
                    </div>
                    <div style={{
                      color: '#9CA3AF',
                      fontSize: '14px'
                    }}>
                      From {formatCurrency(commission.paymentAmount)} performance
                    </div>
                  </div>
                  <div style={{
                    color: '#6B7280',
                    fontSize: '12px',
                    textAlign: 'right'
                  }}>
                    {formatDate(commission.createdAt)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* How it Works */}
        <div style={{
          backgroundColor: '#1E3A8A',
          borderRadius: '16px',
          padding: '24px',
          border: '3px solid #3B82F6',
          marginTop: '24px'
        }}>
          <h3 style={{
            color: '#93C5FD',
            fontSize: '20px',
            fontWeight: 'bold',
            margin: '0 0 16px 0',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span>💡</span>
            How it Works
          </h3>
          
          <div style={{
            color: '#BFDBFE',
            fontSize: '14px',
            lineHeight: '1.6'
          }}>
            <div style={{ marginBottom: '12px' }}>
              <strong>1.</strong> Share your referral code with other musicians
            </div>
            <div style={{ marginBottom: '12px' }}>
              <strong>2.</strong> When they sign up and enter your code, you become their referrer
            </div>
            <div style={{ marginBottom: '12px' }}>
              <strong>3.</strong> You earn 2% commission from their earnings for 12 months after their first paid performance
            </div>
            <div style={{ marginBottom: '12px' }}>
              <strong>4.</strong> Commissions are added to your total earnings daily and can be redeemed as gift cards
            </div>
            <div style={{
              marginTop: '16px',
              padding: '12px',
              backgroundColor: '#1E40AF',
              borderRadius: '8px',
              fontSize: '12px'
            }}>
              💎 <strong>Pro Tip:</strong> The more active musicians you refer, the more you earn. Quality referrals perform better than quantity!
            </div>
          </div>
        </div>
      </div>
      
      {/* Referral Management Section */}
      <div style={{ height: '24px' }}></div>
      <ReferralManagement />
      
      {/* Show ReferralStatus at bottom if referral code exists */}
      {referralData?.hasReferralCode && (
        <>
          <div style={{ height: '24px' }}></div>
          <ReferralStatus user={user} />
        </>
      )}
    </div>
  );
};

export default ReferralDashboard;